/**
 * GROUP BY for client-side grids — fleet filter contract v2 §13.3 (RULES M3–M7).
 *
 * Pure: takes the already filtered AND sorted rows and partitions them. It never changes which rows
 * match (M6) and never reorders rows inside a group (M7 — the active sort already did that).
 *
 * 🔑 **Bucketing resolves in the STAFF-INTERACTIVE zone, `America/New_York` (M5), through the IANA
 * zone, never a fixed offset.** A row stamped 2026-08-19T01:00Z is 21:00 ET on the 18th; filed under
 * the 19th it would sit in a day its own *Today* filter excludes.
 *
 * ⚠ **Except for DATE-ONLY columns** (`FilterColumn.dateOnly`). A review period's start is a calendar
 * DAY stored as UTC midnight; read through Eastern time it is the previous evening, and May 1 would
 * be filed under April 30 — the exact defect `calendarDate.ts` exists to prevent. Those columns bucket
 * on their UTC calendar day. The contract's M5 is about instants; a date-only value is not one.
 */
import type { FilterColumn } from './types';
import type { GroupGranularity } from './viewDefinition';
import { STAFF_ZONE } from './viewQuery';

export const BLANK_GROUP_LABEL = '(blank)';

export interface RowGroup<T> {
  /** Stable sort key for the group (chronological for dates, alphabetical otherwise; blank last). */
  key: string;
  label: string;
  rows: T[];
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAY_OFFSET: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };

const fmtCache = new Map<string, Intl.DateTimeFormat>();
function partsIn(zone: string, d: Date) {
  let f = fmtCache.get(zone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone: zone, year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'short' });
    fmtCache.set(zone, f);
  }
  const p = f.formatToParts(d);
  const get = (t: string) => p.find((x) => x.type === t)?.value ?? '';
  return { y: Number(get('year')), m: Number(get('month')), d: Number(get('day')), weekday: get('weekday') };
}

function pad(n: number) {
  return String(n).padStart(2, '0');
}

/** The bucket of one date value, as { key, label }, or null when the value is not a date. */
export function dateBucket(
  value: unknown,
  granularity: GroupGranularity,
  dateOnly = false
): { key: string; label: string } | null {
  if (value === null || value === undefined || value === '') return null;
  const t = value instanceof Date ? value.getTime() : Date.parse(String(value));
  if (Number.isNaN(t)) return null;
  const { y, m, d, weekday } = partsIn(dateOnly ? 'UTC' : STAFF_ZONE, new Date(t));

  switch (granularity) {
    case 'year':
      return { key: `${y}`, label: `${y}` };
    case 'quarter': {
      const q = Math.floor((m - 1) / 3) + 1;
      return { key: `${y}-Q${q}`, label: `Q${q} ${y}` };
    }
    case 'month':
      return { key: `${y}-${pad(m)}`, label: `${MONTHS[m - 1]} ${y}` };
    case 'week': {
      // Monday-based (M4), identical to {{weekStart}}: Sunday belongs to the week that began the
      // preceding Monday. Calendar arithmetic on the zoned date, so DST cannot leak in.
      const monday = new Date(Date.UTC(y, m - 1, d - (WEEKDAY_OFFSET[weekday] ?? 0)));
      const [my, mm, md] = [monday.getUTCFullYear(), monday.getUTCMonth() + 1, monday.getUTCDate()];
      return { key: `${my}-${pad(mm)}-${pad(md)}`, label: `Week of ${MONTHS[mm - 1]} ${md}, ${my}` };
    }
    case 'day':
      return { key: `${y}-${pad(m)}-${pad(d)}`, label: `${MONTHS[m - 1]} ${d}, ${y}` };
  }
}

/**
 * Partition `rows` by `column`. Date columns bucket by `granularity` (month when absent — V1).
 * Rows whose key is null form ONE "(blank)" group, last — never dropped (M6), so the group counts
 * always sum to the filtered row count (C63).
 */
export function groupRows<T>(
  rows: readonly T[],
  column: FilterColumn<T>,
  granularity: GroupGranularity | null
): RowGroup<T>[] {
  const isDate = column.filterType === 'date-range';
  const groups = new Map<string, RowGroup<T>>();
  const blank: RowGroup<T> = { key: '￿', label: BLANK_GROUP_LABEL, rows: [] };

  for (const row of rows) {
    const raw = column.getValue ? column.getValue(row) : undefined;
    let bucket: { key: string; label: string } | null;
    if (isDate) {
      bucket = dateBucket(raw, granularity ?? 'month', column.dateOnly === true);
    } else if (raw === null || raw === undefined || String(raw).trim() === '') {
      bucket = null;
    } else {
      const text = String(raw);
      const label = column.filterOptions?.find((o) => o.value === text)?.label ?? text;
      bucket = { key: label.toLocaleLowerCase(), label };
    }

    if (!bucket) {
      blank.rows.push(row);
      continue;
    }
    const existing = groups.get(bucket.key);
    if (existing) existing.rows.push(row);
    else groups.set(bucket.key, { key: bucket.key, label: bucket.label, rows: [row] });
  }

  const ordered = [...groups.values()].sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  return blank.rows.length > 0 ? [...ordered, blank] : ordered;
}

/** Whether a column may be grouped: picklist, text, boolean and date columns with a value accessor. */
export function isGroupable<T>(column: FilterColumn<T>): boolean {
  return !!column.getValue && !!column.filterType && column.filterType !== 'number';
}
